import { useAuth0 } from "@auth0/auth0-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { customClient } from "@utils/helpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { useDebounce } from "use-debounce";

import { Alert, Button, Headline } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportReportTypeChoices,
  GetReportsWithReviewStateDocument,
  ReportReviewView,
  ReportTypeEdge,
  ReportTypeEnums,
  useReportReviewCountsQuery,
} from "@api/graphql/types-and-hooks";
import { ClinicalNewReportDialog } from "@components/ClinicalNewReportDialog/ClinicalNewReportDialog";
import {
  ClinicalReport,
  ClinicalReportsTable,
  StatusType,
} from "@components/ClinicalReportsTable/ClinicalReportsTable";
import { SavedGoalsBank } from "@components/GoalsBank/SavedGoals/SavedGoalsBank";
import { RecommendationsBank } from "@components/RecommendationsBank/RecommendationsBank";
import { REVIEW_AND_SIGN_ROUTE } from "@components/ReportField/consts";
import { REVIEW_PARAM } from "@components/ReportReview/reviewViews";
import "@components/notifications/notifications.css";
import { useCreateReportHandler } from "@hooks/useCreateReportHandler";
import { useRecentlyViewedReports } from "@hooks/useRecentlyViewedReports";
import { useAdminData } from "@providers/AdminDataProvider";
import { getStatusAndRedirectRouteFromReport as getIAStatusAndRedirectRoute } from "@routes/AssessmentReportsV2/AssessmentReportsHelper";
import { getStatusAndRedirectRouteFromReport } from "@routes/ClinicalReports/ClinicalReportsHelper";
import { parseReviewParam, reviewParamFromView } from "@routes/ClinicalReports/reviewFilter";

export const buildIaMergeRedirectPath = (pathname: string, assessmentId: string): string => {
  const expectedPrefix = `/assessment-reports/${assessmentId}`;
  if (!pathname.startsWith(expectedPrefix)) {
    return `/clinical-reports/assessment-reports/${assessmentId}`;
  }
  const subPath = pathname.slice(expectedPrefix.length);
  return `/clinical-reports/assessment-reports/${assessmentId}${subPath}`;
};

export const computeReportTypeFilter = (
  flags: Pick<FeatureFlags, "db-single-page" | "ia-clinical-merge">,
  hasBcbaRole: boolean,
  hasPsychologistRole: boolean,
): ReportTypeEnums | undefined => {
  if (!flags["db-single-page"]) {
    if (flags["ia-clinical-merge"] && hasBcbaRole) return undefined;
    return ReportTypeEnums.Reassessment;
  }
  if (hasPsychologistRole && !hasBcbaRole) return ReportTypeEnums.DiagnosisEvaluation;
  if (hasBcbaRole && !hasPsychologistRole) {
    if (flags["ia-clinical-merge"]) return undefined;
    return ReportTypeEnums.Reassessment;
  }
  return undefined;
};

export const computeAllowedReportTypes = (
  flags: Pick<FeatureFlags, "db-single-page" | "ia-clinical-merge">,
  hasBcbaRole: boolean,
  hasPsychologistRole: boolean,
): ReportTypeEnums[] => {
  // When the API filter returns a specific type, no client-side filtering needed
  if (computeReportTypeFilter(flags, hasBcbaRole, hasPsychologistRole) !== undefined) return [];
  const types: ReportTypeEnums[] = [];
  if (flags["db-single-page"] && hasPsychologistRole) types.push(ReportTypeEnums.DiagnosisEvaluation);
  if (hasBcbaRole) types.push(ReportTypeEnums.Reassessment);
  if (flags["ia-clinical-merge"] && hasBcbaRole) types.push(ReportTypeEnums.InitialAssessment);
  return types;
};

export const computePageTitle = (
  flags: Pick<FeatureFlags, "db-single-page" | "ia-clinical-merge">,
  hasBcbaRole: boolean,
  hasPsychologistRole: boolean,
): string => {
  if (flags["db-single-page"]) {
    if (hasPsychologistRole && !hasBcbaRole) return "Evaluations";
    if (hasBcbaRole && !hasPsychologistRole) return flags["ia-clinical-merge"] ? "Clinical Reports" : "Reassessment";
    return "Clinical Reports";
  }
  return flags["ia-clinical-merge"] && hasBcbaRole ? "Clinical Reports" : "Reassessment";
};

export const mapReports = (edges: ReportTypeEdge[]): ClinicalReport[] => {
  return edges
    .map((edge) => {
      const node = edge?.node;
      if (!node?.id) return null;

      const clientFirstName = node.reportClientDetails?.clientFirstName || "N/A";
      const clientLastName = node.reportClientDetails?.clientLastName || "";
      const clientName = `${clientFirstName} ${clientLastName}`;
      const isReassessment = node.reportType === ApiReportReportTypeChoices.Reassessment;
      const isInitialAssessment = node.reportType === ApiReportReportTypeChoices.InitialAssessment;

      const isKnownType =
        isInitialAssessment || isReassessment || node.reportType === ApiReportReportTypeChoices.DiagnosisEvaluation;
      if (!isKnownType) {
        console.warn("[mapReports] Unhandled reportType:", node.reportType);
      }

      const { redirectRoute, status } = isInitialAssessment
        ? (() => {
            const result = getIAStatusAndRedirectRoute(node);
            // IA helper emits the simplified lifecycle (Draft, Complete,
            // Submitted, Needs Revision, Approved); all five exist on the
            // ClinicalReportsTable StatusType, so we cast across.
            return {
              redirectRoute: result.redirectRoute,
              status: result.status as ClinicalReport["status"],
            };
          })()
        : getStatusAndRedirectRouteFromReport(node, {
            isReassessment,
          });

      const createdByFirst = node?.createdBy?.provider?.providermetadata?.firstName || "";
      const createdByLast = node?.createdBy?.provider?.providermetadata?.lastName || "";
      const createdBy = node?.reportWriter || [createdByFirst, createdByLast].filter(Boolean).join(" ") || "—";

      const report: ClinicalReport = {
        id: node?.id,
        clientName,
        reportType: node?.reportType,
        reportTemplate: node?.reportTemplate || null,
        createdBy,
        dateCreated: new Date(node?.createdDate),
        lastUpdated: new Date(node?.lastUpdatedDate),
        status,
        reviewState: node.myReviewState ?? null,
        redirectRoute,
      };

      return report;
    })
    .filter(Boolean) as ClinicalReport[];
};

// The `/clinical-reports` sub-path a report opens under, by type. Reassessments
// are the default; evaluations and (ia-clinical-merge) initial assessments live
// under their own segments.
const getReportTypePath = (reportType: ApiReportReportTypeChoices): string => {
  switch (reportType) {
    case ApiReportReportTypeChoices.DiagnosisEvaluation:
      return "evaluations";
    case ApiReportReportTypeChoices.InitialAssessment:
      return "assessment-reports";
    case ApiReportReportTypeChoices.Reassessment:
      return "reassessment";
    default:
      console.warn("[getReportTypePath] Unhandled reportType:", reportType);
      return "reassessment";
  }
};

// Absolute route a report opens to (type segment + id + the lifecycle leaf
// `redirectRoute`). Single source of truth so the table, the homepage, and the
// recently-viewed history all navigate to the same place.
export const getClinicalReportPath = (report: Pick<ClinicalReport, "id" | "reportType" | "redirectRoute">): string =>
  `/clinical-reports/${getReportTypePath(report.reportType)}/${report.id}/${report.redirectRoute}`;

// Base route for a report (no lifecycle leaf). The `:reportId` index route
// resolves the correct step itself via ClinicalReportDetailsRedirect, so callers
// that don't want to compute `redirectRoute` client-side — e.g. the Recently
// Viewed widget, which fetches only minimal report data — can navigate here.
export const getClinicalReportBasePath = (report: Pick<ClinicalReport, "id" | "reportType">): string =>
  `/clinical-reports/${getReportTypePath(report.reportType)}/${report.id}`;

// A draft opened under a Review filter view lands on Review & Sign, where Checkpoint is (PROA-3274). Only a draft
// whose usual step is the written report moves; an initial assessment ready for review already opens on Review &
// Sign, and every other row (completed, importing, not yet written) keeps its usual step.
const getReviewRowPath = (row: ClinicalReport): string =>
  getClinicalReportPath(
    row.status === "Draft" && row.redirectRoute === "report-page"
      ? { ...row, redirectRoute: REVIEW_AND_SIGN_ROUTE }
      : row,
  );

export const ClinicalReports: React.FC = () => {
  const go = useNavigate();
  const flags = useFlags<FeatureFlags>();
  const { roles, loginProviderDetailsQuery, isOrgPeerReviewEnabled, isOrgDetailsLoaded } = useAdminData();
  const currentUserId = loginProviderDetailsQuery.data?.getLoginProvider?.auth0User?.id;
  const { recordView } = useRecentlyViewedReports();

  // Determine dynamic page title based on user roles
  const hasPsychologistRole = roles.includes("psychologist") || roles.includes("evaluation-psychologist");
  const hasBcbaRole = roles.includes("assessment-bcba") || roles.includes("bcba");

  const pageTitle = computePageTitle(flags, hasBcbaRole, hasPsychologistRole);

  const reportTypeFilter = computeReportTypeFilter(flags, hasBcbaRole, hasPsychologistRole);

  // flags object reference is unstable across renders; pin deps to the specific values this function reads
  const allowedReportTypes = useMemo(
    () => computeAllowedReportTypes(flags, hasBcbaRole, hasPsychologistRole),
    [flags["ia-clinical-merge"], flags["db-single-page"], hasBcbaRole, hasPsychologistRole],
  );

  const {
    isNewReportDialogOpen,
    setNewReportDialogOpen,
    selectedClientId,
    setSelectedClientId,
    selectedTemplate,
    setSelectedTemplate,
    getClientReport,
    createReportObject,
    goToReport,
    isReportCreating,
    setSelectedReportType,
    selectedReportType,
    learnerName,
    setLearnerName,
    learnerQuery,
  } = useCreateReportHandler();

  const { getAccessTokenSilently } = useAuth0();
  const [tableData, setTableData] = useState<ClinicalReport[]>([]);
  const pageItemsDefault: number = 20;
  const pageItemsRef = useRef<number>(pageItemsDefault);
  const [pageItems, setPageItems] = useState<number>(pageItemsDefault);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalItemCount, setTotalItemCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<StatusType[]>([]);
  const [typeFilter, setTypeFilter] = useState<ReportTypeEnums[]>([]);
  const [myReportsOnly, setMyReportsOnly] = useState<boolean>(false);
  const createdById = myReportsOnly ? currentUserId : undefined;

  // useIsPeerReviewEnabled needs a report in context (QA scanner flags are per report type), so the list gates on
  // the flag plus the organization's Team Review setting.
  const isPeerReviewEnabled = !!flags["report-peer-review"] && isOrgPeerReviewEnabled;
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewView = isPeerReviewEnabled ? parseReviewParam(searchParams.get(REVIEW_PARAM)) : undefined;
  // A ?review= link can't be honored until the org's Team Review setting is known; hold the list instead of
  // flashing it unfiltered.
  const isAwaitingReviewSetting =
    !!flags["report-peer-review"] && !!searchParams.get(REVIEW_PARAM) && isOrgDetailsLoaded === false;
  const setReviewView = (next: ReportReviewView | undefined) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next) params.set(REVIEW_PARAM, reviewParamFromView(next));
        else params.delete(REVIEW_PARAM);
        return params;
      },
      { replace: true },
    );
  const reviewCountsQuery = useReportReviewCountsQuery(undefined, {
    enabled: isPeerReviewEnabled,
    refetchOnMount: "always",
  });

  // add debounce to delay querying until user stops typing
  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);

  // Reset to page 1 when the query's inputs change. useInfiniteQuery rebuilds
  // `data.pages` from scratch on a queryKey change, so a leftover pageNumber
  // from the previous query would point at an index that no longer exists in
  // the new data, and the rebuild effect below would leave tableData stale.
  useEffect(() => {
    setPageNumber(1);
    setTableData([]);
  }, [debouncedSearchTerm, reportTypeFilter, createdById, reviewView]);

  const getReportsQuery = useInfiniteQuery({
    queryKey: ["getReportsWithReviewState", "infinite", debouncedSearchTerm, reportTypeFilter, createdById, reviewView],
    queryFn: async ({ pageParam = { after: null } }) => {
      const { after } = pageParam;
      const variables = {
        first: pageItemsRef.current,
        after,
        records: 500,
        filter: {
          reportType: reportTypeFilter,
          searchTerm: debouncedSearchTerm,
          sortBy: "-last_updated_date",
          createdById,
          reviewView,
        },
      };
      const token = await getAccessTokenSilently();
      const response = await customClient({ token, document: GetReportsWithReviewStateDocument, variables });

      return response.getReports;
    },
    initialPageParam: { after: null },
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage) {
        return {
          after: lastPage.pageInfo.endCursor, // Use the cursor for more efficient pagination
        };
      }
      return undefined; // No more pages to fetch
    },
    // Server-side scoping to the current user relies on createdById, so don't
    // fire the (unscoped) query until it's resolved once "My Reports" is on —
    // otherwise the toggle reads as active while every org report still shows.
    enabled: (!myReportsOnly || !!currentUserId) && !isAwaitingReviewSetting,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    staleTime: Infinity,
    gcTime: 0, // immediately garbage collect old data
  });

  const { data, refetch, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, isError } = getReportsQuery;
  // callback function used by the table when a page change occurs
  const getCurrentPageData = (currentPage: number, itemsPerPage: number) => {
    // refetch items if the number of items per page is greater than the current
    if (itemsPerPage > pageItemsRef.current) {
      pageItemsRef.current = itemsPerPage;
      setPageItems(itemsPerPage);
      refetch();
      return;
    }

    // check if the page number is higher than the current, fetch the next page.
    // the table component will handle decreasing page #
    if (currentPage > pageNumber) {
      fetchNextPage();
      setPageNumber(currentPage);
      return;
    }
    setPageNumber(currentPage);
    setPageItems(itemsPerPage);
  };

  const isFiltering = statusFilter.length > 0 || typeFilter.length > 0;

  // Emit only the current page's rows to `tableData`. The Mosaic <Table>
  // renders whatever `data` we hand it (it doesn't slice — it just paginates
  // the visible page via `totalItemCount`), so accumulating across pages
  // would show page 1's rows alongside page 2's. Skip entirely while
  // filtering — `allLoadedReports` drives the filtered view and `tableData`
  // is unused.
  useEffect(() => {
    if (isFiltering || !data?.pages) return;

    const currentPage = data.pages[pageNumber - 1];
    if (!currentPage) return;

    // Filter edges client-side when both roles are present (API only supports single reportType)
    const edges =
      allowedReportTypes.length > 0
        ? currentPage.edges.filter((edge: ReportTypeEdge) =>
            allowedReportTypes.includes(edge?.node?.reportType as unknown as ReportTypeEnums),
          )
        : currentPage.edges;

    setTotalItemCount(currentPage.totalCount ?? 0);
    setTableData(mapReports(edges));
  }, [data, pageNumber, allowedReportTypes, isFiltering]);

  // The status/type filters are derived client-side (status from
  // payorreportsubmissionSet, type from rules computed against role + flags),
  // so the API can't filter them server-side today. While a filter is active,
  // eagerly pull every remaining server page so the visible result count
  // reflects the full dataset instead of only the current window.
  // Trade-off: for very large orgs this fans out N sequential GetReports
  // calls on each filter activation. Acceptable while typical org sizes stay
  // in the hundreds; the long-term fix is a backend-supported lifecycle-
  // status filter (tracked separately) so the client can stop fetch-all.
  useEffect(() => {
    if (isFiltering && hasNextPage && !isFetching && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isFiltering, hasNextPage, isFetching, isFetchingNextPage, fetchNextPage]);

  // Flatten every loaded page into a single ClinicalReport[] so the filters
  // apply across the whole dataset, not only the current page's window.
  const allLoadedReports = useMemo<ClinicalReport[]>(() => {
    if (!isFiltering || !data?.pages) return [];
    return data.pages.flatMap((page) => {
      const edges =
        allowedReportTypes.length > 0
          ? page.edges.filter((edge: ReportTypeEdge) =>
              allowedReportTypes.includes(edge?.node?.reportType as unknown as ReportTypeEnums),
            )
          : page.edges;
      return mapReports(edges);
    });
  }, [isFiltering, data, allowedReportTypes]);

  const handleReportRedirect = (row: ClinicalReport) => {
    const path = reviewView ? getReviewRowPath(row) : getClinicalReportPath(row);
    // Record before navigating so the homepage's "Recently Viewed" widget
    // reflects this open — including other BCBAs' reports surfaced in the table.
    recordView(row.id);
    go(path);
  };

  return (
    <>
      <div className="mt-10 flex w-full items-center justify-between">
        <Headline size="medium">{pageTitle}</Headline>
        <div className="flex items-center space-x-2">
          {hasPsychologistRole && <RecommendationsBank />}
          {hasBcbaRole && <SavedGoalsBank />}
          <Button
            text="New Report"
            appearance="primary"
            onClick={() => {
              setNewReportDialogOpen(true);
            }}
          />
          {isNewReportDialogOpen && (
            <ClinicalNewReportDialog
              isNewReportDialogOpen={isNewReportDialogOpen}
              setNewReportDialogOpen={(open) => setNewReportDialogOpen(open)}
              selectedReportType={selectedReportType}
              setSelectedReportType={setSelectedReportType}
              selectedClientId={selectedClientId}
              setSelectedClientId={setSelectedClientId}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
              getClientReport={getClientReport}
              createReportObject={createReportObject}
              goToReport={goToReport}
              isReportCreating={isReportCreating}
              learnerName={learnerName}
              setLearnerName={setLearnerName}
              learnerQuery={learnerQuery}
            />
          )}
        </div>
      </div>
      {!!isError && (
        <div className="mt-3">
          <Alert
            appearance="error"
            title="Couldn't load reports"
            text={
              <div className="flex w-full items-center gap-3">
                <div className="flex-1">
                  <p>There was a problem loading your reports.</p>
                  <p>This is usually temporary.</p>
                </div>
                <Button
                  text="Retry"
                  variant="secondary"
                  buttonStyle="border"
                  size="sm"
                  onClick={() => refetch()}
                  disabled={isFetching}
                />
              </div>
            }
          />
        </div>
      )}
      {isPeerReviewEnabled && reviewCountsQuery.isError && (
        <div className="mt-3">
          <Alert
            appearance="error"
            title="Couldn't load review counts"
            text={
              <div className="flex w-full items-center gap-3">
                <p className="flex-1">The review filter counts are unavailable. The list below is not affected.</p>
                <Button
                  text="Retry"
                  variant="secondary"
                  buttonStyle="border"
                  size="sm"
                  onClick={() => reviewCountsQuery.refetch()}
                  disabled={reviewCountsQuery.isFetching}
                />
              </div>
            }
          />
        </div>
      )}
      <ClinicalReportsTable
        data={
          isFiltering
            ? allLoadedReports.filter(
                (row) =>
                  (statusFilter.length === 0 || statusFilter.includes(row.status)) &&
                  (typeFilter.length === 0 || typeFilter.includes(row.reportType as unknown as ReportTypeEnums)),
              )
            : tableData
        }
        refetch={getReportsQuery.refetch}
        // Keep loading true continuously through the eager fetch-all-pages
        // loop activated on filter; otherwise `isFetchingNextPage` toggles
        // true/false between each page and the loading overlay flashes once
        // per page (~6 flashes for a 3-page dataset).
        loading={isFetching || isFetchingNextPage || (isFiltering && hasNextPage)}
        onRowClick={(row) => handleReportRedirect(row)}
        onPageChange={getCurrentPageData}
        pageItems={pageItems}
        totalItemCount={totalItemCount}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        myReportsOnly={myReportsOnly}
        onMyReportsOnlyChange={setMyReportsOnly}
        review={
          isPeerReviewEnabled
            ? { view: reviewView, onChange: setReviewView, counts: reviewCountsQuery.data?.reportReviewCounts }
            : undefined
        }
      />
    </>
  );
};
